import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useNavigation from "hooks/useNavigation";
import { logEvent } from "lib/events";
import { Currencies, NonProfit } from "@ribondao/shared/types";
import causeIllustration from "assets/images/direct-illustration.svg";
import { useIntegrationContext } from "contexts/integrationContext";
import { useOffers } from "@ribondao/shared/hooks";
import { useLanguage } from "hooks/useLanguage";
import { useCurrentUser } from "contexts/currentUserContext";
import { useTicketsContext } from "contexts/ticketsContext";
import { useUnauthorizedContributionModal } from "hooks/modalHooks/useUnauthorizedContributionModal";
import { useAuthentication } from "contexts/authenticationContext";
import * as S from "../styles";
import NonProfitComponent from "./NonProfitComponent";

type Props = {
  nonProfits: NonProfit[];
};

function NonProfitsListCarousel({ nonProfits }: Props): JSX.Element {
  const { t } = useTranslation("translation", {
    keyPrefix: "donations.causesPage",
  });
  const { navigateTo } = useNavigation();
  const { hasTickets } = useTicketsContext();

  const { signedIn } = useCurrentUser();

  const { offers: offersBrl } = useOffers(Currencies.BRL, false);
  const { offers: offersUsd } = useOffers(Currencies.USD, false);

  const { currentLang } = useLanguage();
  const { isAuthenticated } = useAuthentication();
  const { showUnauthorizedContributionModal } =
    useUnauthorizedContributionModal();

  const currentOffer = () =>
    currentLang === "pt-BR" ? offersBrl?.[0] : offersUsd?.[0];

  const { currentIntegrationId: integrationId } = useIntegrationContext();

  const navigateToCheckout = (nonProfit: NonProfit) => {
    const searchParams = new URLSearchParams({
      integration_id: integrationId?.toString() || "",
      offer: currentOffer()?.priceCents?.toString() ?? "1000",
      target: "non_profit",
      target_id: nonProfit.id.toString(),
      currency: currentLang === "pt-BR" ? "BRL" : "USD",
      subscription: "false",
      from: "DirectCardNgo",
    });

    if (isAuthenticated()) {
      navigateTo({
        pathname: "/promoters/checkout",
        search: searchParams.toString(),
      });
    } else if (signedIn) {
      showUnauthorizedContributionModal();
    } else {
      navigateTo({
        pathname: "auth/sign-in",
      });
    }
  };

  const handleButtonClick = useCallback(
    (nonProfit: NonProfit, from: string) => {
      if (hasTickets) {
        logEvent("donateTicketBtn_start", {
          nonProfitId: nonProfit.id,
          from,
        });
        if (signedIn) {
          navigateTo({ pathname: "/select-tickets", state: { nonProfit } });
        } else {
          navigateTo({
            pathname: "/donation/auth/sign-in",
            state: { nonProfit },
          });
        }
      } else {
        navigateToCheckout(nonProfit);
      }
    },
    [hasTickets, signedIn],
  );

  return (
    <S.NonProfitsListContainer>
      {nonProfits.length > 0 ? (
        <div>
          {nonProfits.map((nonProfit: any) => (
            <NonProfitComponent
              key={nonProfit.id}
              nonProfit={nonProfit}
              onFirstButtonClick={handleButtonClick}
              onSecondButtonClick={navigateToCheckout}
            />
          ))}
        </div>
      ) : (
        <S.EmptySectionContainer>
          <S.EmptySectionBox>
            <S.EmptyImage src={causeIllustration} />
            <S.EmptyTitle>{t("noCauses.title")}</S.EmptyTitle>
          </S.EmptySectionBox>
        </S.EmptySectionContainer>
      )}
    </S.NonProfitsListContainer>
  );
}

export default NonProfitsListCarousel;
